/**
 * DataSource adapter.
 *
 * MVP uses the static JSON files bundled with the app. A future DB provider
 * (Supabase / D1 / Postgres) implements the same interface. Nothing downstream
 * should depend on which provider is in use.
 *
 * See docs/DECISIONS.md ADR-006.
 */
import type {
  AppConfig,
  Counter,
  DataSource,
  MediaRefsIndex,
  Move,
  Position,
} from '../types/domain';

import positionsJson from '../../data/positions.json';
import movesJson from '../../data/moves.json';
import countersJson from '../../data/counters.json';
import configJson from '../../data/config.json';


export class StaticJsonDataSource implements DataSource {
  async positions(): Promise<Position[]> {
    return positionsJson as Position[];
  }
  async moves(): Promise<Move[]> {
    return movesJson as Move[];
  }
  async counters(): Promise<Counter[]> {
    return countersJson as Counter[];
  }
  async config(): Promise<AppConfig> {
    return configJson as unknown as AppConfig;
  }
  /**
   * Fetched, not imported. The clip index is several megabytes — importing
   * it makes tsc infer a type over every ref and run out of heap, and puts
   * the whole thing in the entry bundle. Fetching keeps it off the
   * critical path and lets the browser cache it separately.
   */
  private mediaRefsCache: MediaRefsIndex | null | undefined;
  async mediaRefs(): Promise<MediaRefsIndex | null> {
    if (this.mediaRefsCache !== undefined) return this.mediaRefsCache;
    try {
      const res = await fetch(`${import.meta.env.BASE_URL}data/media-refs.json`);
      this.mediaRefsCache = res.ok ? ((await res.json()) as MediaRefsIndex) : null;
    } catch {
      this.mediaRefsCache = null;
    }
    return this.mediaRefsCache;
  }
}

export const dataSource: DataSource = new StaticJsonDataSource();
